import { useEffect, useState } from "react";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Copy, Trash2, ImageIcon, Box, FileDown } from "lucide-react";
import { useTranslation } from "react-i18next";

function kindIcon(kind) {
  if (kind === "model3d") return Box;
  if (kind === "digital") return FileDown;
  return ImageIcon;
}

function isImage(ct, url) {
  if ((ct || "").startsWith("image/")) return true;
  return /\.(webp|png|jpe?g|gif|svg|ico)(\?|$)/i.test(url || "");
}

export default function AdminMedia() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/merchant/media").then(({ data }) => setItems(data.items || [])).catch((e) => toast.error(extractErr(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allSelected = items.length > 0 && items.every((m) => selected.has(m.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map((m) => m.id)));
  };

  const copyUrl = async (url) => {
    const abs = mediaUrl(url) || url;
    try {
      await navigator.clipboard.writeText(abs);
      toast.success("URL copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const delOne = async (id) => {
    try {
      await api.delete(`/merchant/media/${id}`);
      toast.success("Deleted");
      setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/merchant/media/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} file${data.deleted === 1 ? "" : "s"}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(extractErr(e)); } finally { setDeleting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Media</h1>
          <p className="text-slate-500 text-sm mt-1">All uploads for this store. Copy a URL or delete files you no longer need.</p>
        </div>
        {selected.size > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="rounded-full text-red-600 border-red-200" data-testid="media-bulk-delete"><Trash2 className="w-4 h-4 mr-1" /> Delete {selected.size} selected</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {selected.size} file{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
                <AlertDialogDescription>Places that still use these URLs will show a broken image. This cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={bulkDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="media-bulk-delete-confirm">{deleting ? "Deleting…" : "Delete"}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <Card className="p-0 overflow-hidden border-slate-200/70">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">{t("common.loading")}</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500" data-testid="media-empty">No uploads yet. Images you add on products, settings, and pages will appear here.</div>
        ) : (
          <>
            <div className="px-4 py-2.5 flex items-center gap-3 bg-slate-50/70 text-xs font-medium text-slate-500 border-b border-slate-100">
              <Checkbox checked={allSelected ? true : (selected.size ? "indeterminate" : false)} onCheckedChange={toggleAll} data-testid="media-select-all" />
              <span>Select all ({items.length})</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 p-4">
              {items.map((m) => {
                const Icon = kindIcon(m.kind);
                const showImg = isImage(m.content_type, m.url);
                return (
                  <div key={m.id} className={`rounded-xl border overflow-hidden bg-white ${selected.has(m.id) ? "border-blue-400 ring-2 ring-blue-100" : "border-slate-200"}`} data-testid={`media-card-${m.id}`}>
                    <div className="relative aspect-square bg-slate-50">
                      {showImg ? (
                        <img src={mediaUrl(m.url)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 gap-1">
                          <Icon className="w-8 h-8" />
                          <span className="text-[10px] uppercase">{m.kind}</span>
                        </div>
                      )}
                      <div className="absolute top-2 left-2">
                        <Checkbox checked={selected.has(m.id)} onCheckedChange={() => toggle(m.id)} data-testid={`media-select-${m.id}`} />
                      </div>
                    </div>
                    <div className="p-2 space-y-1.5">
                      <div className="text-[11px] text-slate-600 truncate" title={m.filename}>{m.filename || m.kind}</div>
                      <div className="flex gap-1">
                        <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs flex-1" onClick={() => copyUrl(m.url)} data-testid={`media-copy-${m.id}`}><Copy className="w-3 h-3 mr-1" /> Copy URL</Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-red-600" data-testid={`media-delete-${m.id}`}><Trash2 className="w-3 h-3" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this file?</AlertDialogTitle>
                              <AlertDialogDescription>Anything still using this URL will break.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => delOne(m.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
